import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { WifiOff } from 'lucide-react';
import { useNetworkStatus } from '@/hooks/use-network-status';

/**
 * Shows a top banner when the device is offline so failures like
 * Clerk `net::ERR_INTERNET_DISCONNECTED` are explained in-app, not only in the console.
 */
export function NetworkStatusBanner() {
  const online = useNetworkStatus();
  const prevOnlineRef = useRef<boolean | null>(null);

  useEffect(() => {
    document.documentElement.toggleAttribute('data-network-offline', !online);
    return () => {
      document.documentElement.removeAttribute('data-network-offline');
    };
  }, [online]);

  useEffect(() => {
    const prev = prevOnlineRef.current;
    prevOnlineRef.current = online;

    if (prev === null) {
      if (!online) {
        toast.error('No internet connection', {
          description:
            'Your internet connection seems to be offline. Lurna may not work as intended until you reconnect.',
          duration: 10_000,
          id: 'network-offline',
        });
      }
      return;
    }

    if (prev && !online) {
      toast.error('No internet connection', {
        description:
          'Your internet connection seems to be offline. Lurna may not work as intended until you reconnect.',
        duration: 10_000,
        id: 'network-offline',
      });
    }
    if (!prev && online) {
      toast.success('Hey! You are back online. Please refresh the page to continue using Lurna.', { id: 'network-online', duration: 4000 });
    }
  }, [online]);

  if (online) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-99999 flex items-center gap-2 border-b border-destructive/30 bg-destructive/15 px-4 py-3 text-center text-sm text-foreground backdrop-blur-sm dark:bg-destructive/25"
    >
      <WifiOff className="h-4 w-4 shrink-0 text-foreground" aria-hidden />
      <p className="flex-1 leading-tight  font-medium italic">
        You're internet connection seems to be offline. Lurna may not work as intended until you reconnect.
      </p>
    </div>
  );
}
